import React, { useContext, useState, useEffect } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBBadge,
  MDBAlert,
  MDBIcon,
  MDBChip,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBSpinner
} from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { useFormContext } from '../components/FormProvider';
import { useStepsConfig } from '../contexts/StepsConfigContext';
import { useEventData } from '../hooks/useEventData';
import { useConfig } from '../config/ConfigProvider';
import { DevContext } from '../../../../App';
import { getLocalizedText } from '../../../../utils/localizedText';
import { captureError } from '../../../../utils/sentryHelpers';
import { useIndustry } from '../../../../utils/industryContextHook';

export default function ReviewStep({ onComplete, onSaveLecture, companyId, eventId, lectureId, isSavingLecture }) {
  const { t, i18n } = useTranslation();
  const { goToNextStep, goToPreviousStep, navigateToStep, getStepTitle, getMainStepTitle } = useStepNavigation();
  const { formData } = useFormContext();
  const {
    isFormReadyToSave,
    getValidationSummary,
    validationResult,
    eventConfig,
    getStepValidation,
    isStepComplete
  } = useValidation();
  const { getStepContent, getEntityLabel, getReviewSectionTitle, getReviewLabel } = useConfig();
  const { isDevMode } = useContext(DevContext);
  const { currentIndustry } = useIndustry();
  const stepsConfig = useStepsConfig();

  // Get content from configuration
  const incompleteWarning = getStepContent('review', 'incompleteWarning');
  const incompleteMessage = getStepContent('review', 'incompleteMessage');
  const completeMissingFields = getStepContent('review', 'completeMissingFields');
  const saveDraftQuestion = getStepContent('review', 'saveDraftQuestion');
  const submitButton = getStepContent('review', 'submitButton');
  const saveDraftButton = getStepContent('review', 'saveDraftButton');
  const entityLabel = getEntityLabel('singular');

  // Use the event data hook for topics
  const { getTopicsByIds, fetchTopics, topics, isLoading: isLoadingTopics } = useEventData(eventId, currentIndustry?.id);
  
  // State for saving
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  // State for incomplete warning modal
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);

  const validationSummary = getValidationSummary();
  
  // Fetch topics on mount
  useEffect(() => {
    if (eventId && topics.length === 0) {
      fetchTopics();
    }
  }, [eventId, fetchTopics, topics.length]);
  
  // Get topics for display
  const selectedTopics = getTopicsByIds(formData.topics || []);

  const handleContinue = () => {
    // Show warning modal if form is incomplete
    if (!isReady) {
      setShowIncompleteModal(true);
      return;
    }
    
    // Otherwise proceed with saving
    performSave();
  };
  
  const performSave = async () => {
    setIsSaving(true);
    setShowIncompleteModal(false);
    setSaveError(null);
    
    try {
      // Call the save function passed from parent
      if (onSaveLecture) {
        const savedLecture = await onSaveLecture();
        console.log('Lecture saved successfully:', savedLecture);
      }
      
      onComplete?.();
      goToNextStep();
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'save-lecture',
        consoleMessage: 'Error saving lecture:',
        context: { lectureId, eventId, companyId }
      });
      setSaveError(error.message || t('An error occurred while saving. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditStep = (stepId) => {
    navigateToStep(stepId);
  };

  const getStepValidationStatus = (stepId) => {
    // Use the same logic as the navigation - isStepComplete from ValidationProvider
    // This ensures consistent behavior between navigation checkmarks and review step checkmarks
    return isStepComplete(stepId);
  };

  const formatMultiLanguageContent = (content, maxLength = 300) => {
    if (!content || !Array.isArray(content)) return null;
    
    return content.map((item, index) => {
      const flagCode = item.language === 'de' ? 'DE' : item.language === 'en' ? 'US' : 'DE';
      const truncatedText = item.text && item.text.length > maxLength 
        ? item.text.substring(0, maxLength) + '...' 
        : item.text;
      
      return (
        <span key={index} className="d-inline-flex align-items-center me-3">
          <ReactCountryFlag 
            countryCode={flagCode} 
            svg 
            style={{ width: '1.2em', height: '1.2em', marginRight: '0.3rem' }}
          />
          <span>{truncatedText || t('Not provided')}</span>
        </span>
      );
    });
  };

  const formatSingleLanguageContent = (content, maxLength = 300) => {
    if (!content) return t('Not provided');
    if (typeof content === 'string') {
      return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
    }
    return String(content);
  };

  const formatSpeakers = (speakers) => {
    if (!speakers || !Array.isArray(speakers) || speakers.length === 0) {
      return t('No speakers selected');
    }
    
    return speakers.map((speaker, index) => (
      <span key={index} className="d-inline-flex align-items-center me-3">
        {speaker.image?.fileName ? (
          <img 
            src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.image.fileName}`}
            alt={speaker.image.alt || speaker.firstName + ' ' + speaker.lastName}
            className="rounded-circle me-2"
            style={{ width: '24px', height: '24px', objectFit: 'cover' }}
          />
        ) : (
          <MDBIcon
            fas
            icon="user-alt"
            className="rounded-circle border me-2 d-flex align-items-center justify-content-center"
            style={{ 
              width: '24px', 
              height: '24px', 
              fontSize: '12px',
              backgroundColor: '#f8f9fa'
            }}
          />
        )}
        <span>{speaker.firstName} {speaker.lastName}</span>
      </span>
    ));
  };

  const formatLectureType = (type) => {
    const typeLabels = {
      vip: 'VIP-Vortrag',
      technical: 'Fachvortrag', 
      educational: 'Schulungsvortrag',
      marketing: 'Marketing',
      promotion_technical: 'Technische Promotion'
    };
    return typeLabels[type] || type;
  };

  const formatTopics = (topicIds) => {
    if (!topicIds || !Array.isArray(topicIds) || topicIds.length === 0) {
      return t('No topics selected');
    }
    
    // If topics are still loading, show a loading indicator
    if (isLoadingTopics) {
      return (
        <div className="d-flex align-items-center">
          <MDBSpinner size="sm" className="me-2" />
          <span className="text-muted">{t('Loading topics...')}</span>
        </div>
      );
    }
    
    // Debug logging
    console.log('formatTopics Debug:', {
      topicIds,
      selectedTopics,
      allTopics: topics,
      eventId,
      isLoadingTopics
    });
    
    return (
      <div className="d-flex flex-wrap gap-1">
        {topicIds.map((topicId, index) => {
          const topic = selectedTopics.find(t => t.id === topicId);
          const topicName = topic ? getLocalizedText(topic.name) : null;
          
          // If we can't find the topic and topics are loaded, show just the ID with a warning
          const displayName = topicName || `Themenbereich nicht gefunden (${topicId.substring(0, 8)}...)`;
          
          return (
            <MDBChip 
              key={index}
              size="sm"
              className="mb-1"
              title={`ID: ${topicId}`}
            >
              {displayName}
            </MDBChip>
          );
        })}
      </div>
    );
  };

  const formatGeneralConditions = (acceptedConditions) => {
    if (!acceptedConditions || acceptedConditions.length === 0) {
      return t('No conditions accepted');
    }

    // Get the general conditions configuration from event config
    const generalConditions = eventConfig?.generalConditions || [];
    
    return (
      <div className="d-flex flex-wrap" style={{ gap: '0.25rem' }}>
        {acceptedConditions.map((acceptedCondition, index) => {
          // Find the full condition details from config
          const condition = generalConditions.find(c => c.id === acceptedCondition.id);
          if (!condition) return null;
          
          // Get the title in current language
          const conditionTitle = getLocalizedText(condition.title) || acceptedCondition.id;
          
          return (
            <MDBChip 
              key={index}
              size="sm"
              className="mb-1"
              style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
            >
              {conditionTitle}
            </MDBChip>
          );
        })}
      </div>
    );
  };

  const formatSubjects = (subjects) => {
    if (!subjects || !Array.isArray(subjects) || subjects.length === 0) {
      return t('No subjects provided');
    }
    
    // Filter out empty subjects and convert to the same format as formatMultiLanguageContent expects
    const validSubjects = subjects.filter(subject => {
      const deText = subject.de || '';
      const enText = subject.en || '';
      return (deText.trim() !== '' && deText !== 'Unterpunkt') || 
             (enText.trim() !== '' && enText !== 'Unterpunkt');
    });
    
    if (validSubjects.length === 0) {
      return t('No subjects provided');
    }
    
    return (
      <div className="d-flex flex-wrap gap-1">
        {validSubjects.map((subject, index) => {
          const deText = subject.de || '';
          const enText = subject.en || '';
          
          // Create array in same format as other multilanguage content
          const languageArray = [];
          if (deText) languageArray.push({ language: 'de', text: deText });
          if (enText) languageArray.push({ language: 'en', text: enText });
          
          return (
            <MDBChip 
              key={index}
              size="sm"
              className="mb-1"
            >
              <span className="fw-bold me-3">{index + 1}.</span>
              {languageArray.map((item, langIndex) => {
                const flagCode = item.language === 'de' ? 'DE' : item.language === 'en' ? 'US' : 'DE';
                const isLast = langIndex === languageArray.length - 1;
                
                return (
                  <span key={langIndex} className={`d-inline-flex align-items-center ${!isLast ? 'me-3' : ''}`}>
                    <ReactCountryFlag 
                      countryCode={flagCode} 
                      svg 
                      style={{ width: '1.2em', height: '1.2em', marginRight: '0.3rem' }}
                    />
                    <span>{item.text}</span>
                  </span>
                );
              })}
            </MDBChip>
          );
        })}
      </div>
    );
  };

  const formatImagePreview = (image) => {
    if (!image?.fileName) {
      return t('No image uploaded');
    }
    
    return (
      <div className="d-flex align-items-center">
        <img 
          src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/lectureImages/${image.fileName}`}
          alt={image.alt || 'Lecture image'}
          className="rounded me-2"
          style={{ width: '90px', height: '50.6px', objectFit: 'cover' }}
        />
      </div>
    );
  };


  // Helper function to check if a step exists in the configuration
  const isStepInConfig = (stepId) => {
    if (!stepsConfig || !Array.isArray(stepsConfig)) return true; // If no config, show all steps

    // Check if stepId exists as a main step or substep
    return stepsConfig.some(step => {
      // Check if it's the main step
      if (step.id === stepId) return true;

      // Check if it's a substep
      if (step.substeps && Array.isArray(step.substeps)) {
        return step.substeps.some(substep => substep.id === stepId);
      }

      return false;
    });
  };

  // Group review items by main steps - all possible items
  const allReviewSections = [
    {
      mainStepId: 'general',
      title: getReviewSectionTitle('general') || 'Allgemeine Angaben',
      items: [
        {
          stepId: 'general_conditions',
          label: getReviewLabel('general_conditions') || 'Vortragsbedingungen',
          content: formData.generalConditions?.filter(c => c.accepted)?.length > 0
            ? formatGeneralConditions(formData.generalConditions.filter(c => c.accepted))
            : 'Keine Bedingungen akzeptiert',
          isValid: getStepValidationStatus('general_conditions')
        },
        {
          stepId: 'lecture_type',
          label: getReviewLabel('lecture_type') || 'Vortragskategorie',
          content: formatLectureType(formData.type),
          isValid: getStepValidationStatus('lecture_type')
        }
      ]
    },
    {
      mainStepId: 'details',
      title: getReviewSectionTitle('details') || 'Vortragsdetails',
      items: [
        {
          stepId: 'language',
          label: getReviewLabel('language') || 'Vortragssprache',
          content: formData.language ? (
            <div className="d-flex align-items-center">
              <ReactCountryFlag
                countryCode={formData.language === 'de' ? 'DE' : formData.language === 'en' ? 'US' : 'DE'}
                svg
                style={{ width: '1.2em', height: '1.2em', marginRight: '0.5rem' }}
              />
              <span>{formData.language === 'de' ? 'Deutsch' : formData.language === 'en' ? 'English' : formData.language}</span>
            </div>
          ) : t('Not specified'),
          isValid: getStepValidationStatus('language')
        },
        {
          stepId: 'title',
          label: getReviewLabel('title') || 'Titel',
          content: formatMultiLanguageContent(formData.title, 100),
          isValid: getStepValidationStatus('title')
        },
        {
          stepId: 'subjects',
          label: getReviewLabel('subjects') || 'Unterpunkte',
          content: formatSubjects(formData.subjects),
          isValid: getStepValidationStatus('subjects')
        },
        {
          stepId: 'description',
          label: getReviewLabel('description') || 'Beschreibung',
          content: formatMultiLanguageContent(formData.content, 300),
          isValid: getStepValidationStatus('description')
        },
        {
          stepId: 'topics',
          label: getReviewLabel('topics') || 'Themenbereich',
          content: formatTopics(formData.topics),
          isValid: getStepValidationStatus('topics')
        },
        {
          stepId: 'speakers',
          label: getReviewLabel('speakers') || 'Referenten',
          content: formatSpeakers(formData.speakers),
          isValid: getStepValidationStatus('speakers')
        },
        {
          stepId: 'image',
          label: getReviewLabel('image') || 'Bild',
          content: formatImagePreview(formData.image),
          isValid: getStepValidationStatus('image')
        },
        {
          stepId: 'booth_location',
          label: getReviewLabel('booth_location') || 'Standort',
          content: formData.companyBooth ? (
            <div className="d-flex align-items-center">
              <MDBIcon fas icon="map-marker-alt" className="text-success me-2" />
              <span>Stand {formData.companyBooth}</span>
            </div>
          ) : t('Kein Standort ausgewählt'),
          isValid: getStepValidationStatus('booth_location')
        },
        {
          stepId: 'handout',
          label: getReviewLabel('handout') || 'Handout',
          content: formData.handout ? (
            <div className="d-flex align-items-center">
              {formData.handout.de && (
                <span className="d-inline-flex align-items-center me-3">
                  <ReactCountryFlag
                    countryCode="DE"
                    svg
                    style={{ width: '1.2em', height: '1.2em', marginRight: '0.3rem' }}
                  />
                  <span>{formData.handout.de.fileName || 'PDF'}</span>
                </span>
              )}
              {formData.handout.en && (
                <span className="d-inline-flex align-items-center">
                  <ReactCountryFlag
                    countryCode="US"
                    svg
                    style={{ width: '1.2em', height: '1.2em', marginRight: '0.3rem' }}
                  />
                  <span>{formData.handout.en.fileName || 'PDF'}</span>
                </span>
              )}
            </div>
          ) : t('No handout uploaded'),
          isValid: getStepValidationStatus('handout')
        },
        {
          stepId: 'presentation',
          label: getReviewLabel('presentation') || 'Präsentation',
          content: formData.presentation?.fileName
            ? `Datei: ${formData.presentation.fileName}`
            : t('No presentation uploaded'),
          isValid: getStepValidationStatus('presentation')
        }
      ]
    }
  ];

  // Filter review sections to only include steps that are in the configuration
  const reviewSections = allReviewSections.map(section => ({
    ...section,
    items: section.items.filter(item => isStepInConfig(item.stepId))
  })).filter(section => section.items.length > 0); // Remove sections with no items
  
  const getIncompleteSteps = () => {
    const incompleteSteps = [];
    
    reviewSections.forEach(section => {
      section.items.forEach(item => {
        if (!item.isValid && item.stepId !== 'information' && item.stepId !== 'review') {
          incompleteSteps.push({
            stepId: item.stepId,
            label: item.label,
            section: section.title
          });
        }
      });
    });
    
    return incompleteSteps;
  };
  
  // Debug logging
  const incompleteSteps = getIncompleteSteps();
  const isReady = isFormReadyToSave();
  console.log('ReviewStep Debug:', {
    isFormReadyToSave: isReady,
    incompleteSteps: incompleteSteps,
    incompleteCount: incompleteSteps.length,
    validationSummary: validationSummary
  });

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <MDBTypography tag="h3" className="mb-0">
          {t('Review Information')}
        </MDBTypography>
      </div>
      
      {/* Save Error Alert */}
      {saveError && (
        <MDBAlert color="danger" dismissible onClose={() => setSaveError(null)}>
          <MDBIcon icon="exclamation-triangle" className="me-2" />
          {saveError}
        </MDBAlert>
      )}
      
      {/* Review Sections */}
      {reviewSections.map((section) => (
        <div key={section.mainStepId} className="mb-4">
          <MDBTypography tag="h5" className="mb-3 text-muted">
            {section.title}
          </MDBTypography>
          
          {section.items.map((item) => (
            <div key={item.stepId} className="mb-3 py-2">
              <div 
                className="d-flex align-items-center" 
                style={{ cursor: 'pointer' }}
                onClick={() => handleEditStep(item.stepId)}
                title={`${t('Edit')} ${item.label}`}
              >
                {/* Status Icon - Fixed width */}
                <div style={{ width: '40px', flexShrink: 0 }} className="text-center">
                  <MDBIcon 
                    icon={item.isValid ? 'check' : 'times'} 
                    className={item.isValid ? 'text-success' : 'text-danger'}
                    size="lg"
                  />
                </div>
                
                {/* Step Label - Fixed width */}
                <div style={{ width: '180px', flexShrink: 0 }} className="pe-3">
                  <span className="fw-bold">{item.label}</span>
                </div>
                
                {/* Content - Takes remaining space */}
                <div className="flex-grow-1 pe-3">
                  <div className="text-dark">
                    {React.isValidElement(item.content) ? item.content : (
                      <span>{item.content}</span>
                    )}
                  </div>
                </div>
                
                {/* Edit Icon - Fixed width */}
                <div style={{ width: '60px', flexShrink: 0 }} className="text-end">
                  <MDBIcon 
                    icon="edit" 
                    size="sm" 
                    className="text-primary"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
      
      {/* Dev Mode Debug Info */}
      {isDevMode && (
        <>
          {/* Overall Validation Status */}
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-primary">
                <MDBCardBody>
                  <MDBTypography tag="h5" className="mb-3">
                    📋 Gesamtvalidierung (Dev Mode)
                  </MDBTypography>
                  
                  <div className="mb-3">
                    <strong>Status:</strong>{' '}
                    <MDBBadge color={validationSummary.isValid ? 'success' : 'danger'} size="lg">
                      {validationSummary.isValid ? 'Bereit zum Speichern' : 'Nicht vollständig'}
                    </MDBBadge>
                  </div>
                  
                  <div className="mb-3">
                    <strong>Event:</strong> {eventConfig.eventId} ({eventConfig.eventType})
                  </div>
                  
                  <div className="mb-3">
                    <strong>Erforderliche Felder:</strong>{' '}
                    {eventConfig.requiredFields?.join(', ') || 'none'}
                  </div>
                  
                  {!validationSummary.isValid && (
                    <div className="mb-3">
                      <MDBAlert color="warning" className="mb-0">
                        <strong>Fehler gefunden:</strong> {validationSummary.totalErrors} Validierungsfehler
                      </MDBAlert>
                    </div>
                  )}
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>

          {/* Validation Details */}
          <MDBRow className="mb-4">
            <MDBCol>
              <MDBCard className="border-info">
                <MDBCardBody>
                  <MDBTypography tag="h6" className="mb-3">
                    🔍 Detaillierte Validierungsergebnisse (Dev Mode)
                  </MDBTypography>
                  
                  {validationSummary.isValid ? (
                    <MDBAlert color="success">
                      ✅ Alle Validierungen bestanden! Formular kann gespeichert werden.
                    </MDBAlert>
                  ) : (
                    <>
                      <div className="mb-3">
                        <strong>Fehler nach Schritten:</strong>
                        {Object.entries(validationSummary.errorsByStep).map(([stepId, errors]) => (
                          <div key={stepId} className="ms-3 mb-2">
                            <strong>{stepId}:</strong>
                            <ul className="mb-0">
                              {errors.map((error, index) => (
                                <li key={index}>
                                  <code>{error.field}</code>: {error.message}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      
                      {validationSummary.criticalErrors.length > 0 && (
                        <div className="mb-3">
                          <MDBAlert color="danger">
                            <strong>Kritische Fehler:</strong> {validationSummary.criticalErrors.length} 
                            erforderliche Felder sind nicht korrekt ausgefüllt.
                          </MDBAlert>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="mt-3">
                    <strong>Aktuelle Formulardaten:</strong>
                    <pre className="bg-light p-2 mt-2" style={{fontSize: '0.8rem', maxHeight: '200px', overflow: 'auto'}}>
                      {JSON.stringify(formData, null, 2)}
                    </pre>
                  </div>
                  
                  <div className="mt-3">
                    <strong>Vollständiges Validierungsergebnis:</strong>
                    <pre className="bg-light p-2 mt-2" style={{fontSize: '0.8rem', maxHeight: '200px', overflow: 'auto'}}>
                      {JSON.stringify(validationResult, null, 2)}
                    </pre>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        </>
      )}
      
      {/* Warning for incomplete lectures */}
      {!isReady && incompleteSteps.length > 0 && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBAlert color="warning">
              <h5 className="alert-heading">
                <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                {incompleteWarning || `${t("Warning")} - ${t("Lecture Incomplete")}`}
              </h5>
              <p className="mb-2">
                {incompleteMessage || t("Your lecture is still incomplete and will not be considered for program planning in this state.")}
              </p>
              <p className="mb-3 fw-bold">
                {completeMissingFields || t("Please complete the following points to approve your lecture for program planning")}:
              </p>
              <ul className="mb-0">
                {incompleteSteps.map((step, index) => (
                  <li key={index}>
                    <strong>{step.label}</strong> ({t("in")} {step.section})
                  </li>
                ))}
              </ul>
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Incomplete Form Modal */}
      <MDBModal open={showIncompleteModal} onClose={() => setShowIncompleteModal(false)} staticBackdrop>
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="exclamation-triangle" className="text-warning me-2" />
                {incompleteWarning?.split(' - ')[1] || t("Lecture Incomplete")}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setShowIncompleteModal(false)}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <p className="mb-3">
                <strong>{t("Warning")}:</strong> {incompleteMessage || t("Your lecture is still incomplete and will not be considered for program planning in this state.")}
              </p>
              <p className="mb-3">
                <strong>{t("The following points still need to be completed")}:</strong>
              </p>
              <ul className="mb-3">
                {incompleteSteps.map((step, index) => (
                  <li key={index}>
                    <strong>{step.label}</strong>
                  </li>
                ))}
              </ul>
              <p className="mb-0">
                {saveDraftQuestion || `${t("Do you want to save the lecture as a draft anyway")}?`}
              </p>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="link" className="text-danger" onClick={() => setShowIncompleteModal(false)}>
                <MDBIcon fas icon="times" className="me-2" />
                {t("Cancel")}
              </MDBBtn>
              <MDBBtn color="success" onClick={performSave} disabled={isSaving || isSavingLecture}>
                {(isSaving || isSavingLecture) ? (
                  <>
                    <MDBSpinner size="sm" role="status" className="me-2" />
                    {t("Saving...")}
                  </>
                ) : (
                  <>
                    <MDBIcon fas icon="save" className="me-2" />
                    {saveDraftButton || t("Save as Draft")}
                  </>
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn color="secondary" onClick={goToPreviousStep} outline disabled={isSaving}>
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={isSaving || isSavingLecture}
                color="success"
              >
                {(isSaving || isSavingLecture) ? (
                  <>
                    <MDBIcon fas icon="spinner" spin className="me-2" />
                    {t("Saving")}...
                  </>
                ) : (
                  isReady ? (submitButton || t("Save & Continue")) : (saveDraftButton || t("Save as Draft"))
                )}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}